<script setup lang="ts">
import type { IProgramSuccessMonth, ProgramSuccessInterval, ProgramSuccessOutcome } from '@/types/cpm/programSuccess'
import { getCssVarValue } from '@/util'
import { useDark } from '@vueuse/core'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'

/**
 * Vertical bar chart of monthly transmission-fee-avoidance participation.
 * Each bar's height is the dispatched (curtailed) hours for that month and its
 * colour reflects the outcome: green = success, red = fail, grey = undetermined.
 *
 * @prop months - Chronological per-month entries from the program-success endpoint.
 */
const props = defineProps<{ months?: IProgramSuccessMonth[]; loading?: boolean }>()

/** Selected aggregation interval — two-way bound to the tabs in the panel header. */
const interval = defineModel<ProgramSuccessInterval>('interval')

const emit = defineEmits<{
  /** A bar was clicked; carries that month's year/month for drill-down. */
  (e: 'select-month', payload: { year: number; month: number }): void
}>()

const INTERVAL_TABS: { name: ProgramSuccessInterval; label: string }[] = [
  { name: 'ytd', label: 'YTD' },
  { name: 'last_12_months', label: '1Y' },
  { name: 'all', label: 'ALL' },
]

// Only the modules actually used are registered — tree-shaking keeps the bundle lean.
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend)

/** Fixed colours per outcome. */
const OUTCOME_COLORS: Record<ProgramSuccessOutcome, string> = {
  success: '#639c46',
  fail: '#f95b00',
  undetermined: '#5a5a5a',
}

const OUTCOME_LABELS: Record<ProgramSuccessOutcome, string> = {
  success: 'Success',
  fail: 'Fail',
  undetermined: 'Undetermined',
}

/**
 * Legend entries rendered beneath the chart, in display order. Covers every outcome
 * the bars can take — `undetermined` months (future months, or ones with no CP-hour
 * data yet) render grey, so they need a key too.
 */
const LEGEND_ITEMS: { outcome: ProgramSuccessOutcome; label: string; color: string }[] = (
  ['success', 'fail', 'undetermined'] as ProgramSuccessOutcome[]
).map((outcome) => ({ outcome, label: OUTCOME_LABELS[outcome], color: OUTCOME_COLORS[outcome] }))

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const monthList = computed(() => props.months ?? [])

// Adaptive x-axis label density: show every label for short ranges, thinning
// out as the range grows so labels don't overlap. ≤6 months → all, ≤10 → every
// 2nd, else every 3rd. Driven by how many bars the current interval renders.
const labelStep = computed(() => {
  const monthCount = monthList.value.length
  if (monthCount <= 8) return 1
  if (monthCount <= 16) return 2
  if (monthCount <= 26) return 3
  if (monthCount <= 36) return 4
  if (monthCount <= 48) return 5
  return 6
})

/** e.g. "Jul '25" */
const labels = computed(() =>
  monthList.value.map((m) => `${SHORT_MONTHS[m.month - 1] ?? m.month} '${String(m.year).slice(-2)}`)
)

const chartData = computed<ChartData<'bar', number[], string>>(() => ({
  labels: labels.value,
  datasets: [
    {
      label: 'Dispatched Hours',
      data: monthList.value.map((m) => m.dispatched_hours),
      backgroundColor: monthList.value.map((m) => OUTCOME_COLORS[m.outcome]),
      borderRadius: 3,
      borderSkipped: false,
      maxBarThickness: 48,
    },
  ],
}))

const isDark = useDark()

const chartOptions = computed<ChartOptions<'bar'>>(() => {
  // Match the design's text-secondary exactly: #1a1a1a (light) / #b3b3b3 (dark).
  // The shared --text-secondary token resolves a step lighter in dark mode
  // (grayscale-200), so we pin the raw grayscale tokens here instead. Raw
  // --color-* vars are RGB triplets, hence the rgb() wrap.
  const tickColor = `rgb(${getCssVarValue(isDark.value ? '--color-grayscale-300' : '--color-grayscale-700')})`

  return {
    responsive: true,
    maintainAspectRatio: false,
    // Click a bar to drill the curtailment list into that month.
    onClick: (_event, elements) => {
      const month = monthList.value[elements[0]?.index]
      if (month) emit('select-month', { year: month.year, month: month.month })
    },
    // Pointer cursor while hovering a bar, signalling it's clickable.
    onHover: (event, elements) => {
      const target = event.native?.target as HTMLElement | undefined
      if (target) target.style.cursor = elements.length ? 'pointer' : 'default'
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: tickColor,
          // Thin out labels based on how many months are shown (see labelStep).
          callback(value, index) {
            return index % labelStep.value === 0 ? this.getLabelForValue(Number(value)) : ''
          },
          autoSkip: false,
          // Keep labels horizontal (centered under their bar) rather than diagonal.
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: tickColor,
          // Keep every 3rd auto-generated tick to declutter the axis, suffixed with the hours unit.
          callback: (value, index) => (index % 3 === 0 ? `${value}h` : ''),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} h dispatched`,
        },
      },
    },
  }
})
</script>

<template>
  <div v-loading="loading" class="h-full flex flex-col gap-3">
    <div class="flex items-center justify-between shrink-0">
      <span class="uppercase text-xs text-text-tertiary">Success Rate</span>
      <el-tabs v-model="interval" class="interval-tabs">
        <el-tab-pane v-for="tab in INTERVAL_TABS" :key="tab.name" :name="tab.name" :label="tab.label" />
      </el-tabs>
    </div>

    <div v-if="!months?.length && !loading" class="flex-1 flex items-center justify-center">
      <span class="text-gray-500">No data for the selected range.</span>
    </div>

    <div v-else class="flex-1 min-h-0 flex flex-col">
      <div class="flex-1 min-h-0">
        <Bar :data="chartData" :options="chartOptions" />
      </div>
      <div class="flex justify-center gap-4 pt-2 text-xs text-text-secondary">
        <span v-for="item in LEGEND_ITEMS" :key="item.outcome" class="flex items-center gap-1.5">
          <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: item.color }" />{{ item.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-tabs;

  // Compact interval tabs tucked into the panel header.
  .el-tabs {
    &__nav-scroll {
      padding: 4px;
    }

    &__item {
      padding: 12px 8px !important;
    }
  }
}

.interval-tabs {
  :deep(.el-tabs__header) {
    margin: 0;
  }

  // Drop the full-width baseline so the tabs read as a small standalone control.
  :deep(.el-tabs__nav-wrap::after) {
    display: none;
  }

  :deep(.el-tabs__item) {
    height: 24px;
    font-size: 12px;
    padding: 0 10px;
  }
}
</style>
